import { Component, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DialogShellComponent } from '@custom-components';
import { FilledButtonComponent, TextButtonComponent } from '@custom-components/general/button';
import { TableComponent } from '@custom-components/general/table/table.component';
import { FilterGroup, TableFilterBarComponent } from '@custom-components/general/table-filter-bar';
import { CONNECTION_TYPES, POLLING, SIM } from '@core/constants';
import { TableColumnConfig } from '@core/models/skeleton.model';
import { InstallationGroupService } from '@core/services';
import { InstallationGroupStore } from '@root/app/store/installation-group.store';
import { DeviceStore } from '@root/app/store/device.store';
import {
  deviceTypeFilterGroup,
  installationStateFilterGroup,
} from '@core/utils/device-filter-groups.util';

export interface AddDevicesDialogData {
  groupOid: string;
  /** OID of the partner that owns the group — its devices are the add candidates. */
  ownerPartnerOid: string;
}

type DeviceRow = Record<string, unknown> & { deviceId: string };

/** Members are fetched once (across all pages) so already-grouped devices can be shown disabled. */
const MEMBER_FETCH_LIMIT = 1000;

@Component({
  selector: 'xemex-add-devices-dialog',
  imports: [
    TranslateModule,
    DialogShellComponent,
    FilledButtonComponent,
    TextButtonComponent,
    TableComponent,
    TableFilterBarComponent,
  ],
  providers: [DeviceStore],
  templateUrl: './add-devices-dialog.component.html',
  styleUrl: './add-devices-dialog.component.scss',
})
export class AddDevicesDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<AddDevicesDialogComponent>);
  private readonly groupService = inject(InstallationGroupService);
  private readonly store = inject(InstallationGroupStore);
  private readonly translate = inject(TranslateService);
  protected readonly deviceStore = inject(DeviceStore);
  readonly data = inject<AddDevicesDialogData>(MAT_DIALOG_DATA);

  protected readonly columns: TableColumnConfig[] = [
    { field: 'deviceLDN', label: 'devices.table.deviceLDN', width: '28%', bold: true },
    { field: 'deviceType', label: 'devices.table.type', width: '24%' },
    { field: 'deviceInstallationStatus', label: 'devices.table.installationStatus', width: '28%' },
    { field: 'deviceStatus', label: 'devices.table.status', width: '20%' },
  ];

  protected readonly filterGroups = computed<FilterGroup[]>(() => [
    deviceTypeFilterGroup(this.deviceStore, this.translate),
    installationStateFilterGroup(this.translate),
    {
      key: 'pollingStatus',
      label: this.translate.instant('devices.filter.polling.title'),
      options: POLLING.map(p => p.index),
    },
    {
      key: 'simStatus',
      label: this.translate.instant('devices.filter.SIM.title'),
      options: SIM.map(s => s.index),
    },
    {
      key: 'connectionType',
      label: this.translate.instant('devices.filter.connectionType.title'),
      options: CONNECTION_TYPES.map(c => c.index),
    },
  ]);

  /** Device ids already in the group — shown in the list but not selectable. */
  private readonly memberIds = signal<Set<number>>(new Set());
  /**
   * Members must resolve before any device rows are shown so an already-grouped device is never
   * briefly selectable before being disabled — the table stays loading until then.
   */
  private readonly membersLoaded = signal(false);
  protected readonly isLoading = computed(
    () => !this.membersLoaded() || this.deviceStore.isLoading(),
  );

  /** Selected device ids; persists across server pages via the table's keyed selection. */
  protected readonly selectedKeys = signal<Set<string | number>>(new Set());
  protected readonly selectedCount = computed(() => this.selectedKeys().size);

  protected readonly selectionKey = (row: Record<string, unknown>): number =>
    Number(row['deviceId']);

  protected readonly tableData = computed<DeviceRow[]>(
    () => (this.deviceStore.deviceData()?.devices ?? []) as unknown as DeviceRow[],
  );

  /** Already-grouped devices are shown but not selectable — this dialog only adds, never removes. */
  protected readonly isMemberRow = (row: Record<string, unknown>): boolean =>
    this.memberIds().has(Number(row['deviceId']));

  protected readonly totalCount = computed(() => this.deviceStore.deviceData()?.numberOfItems ?? 0);

  constructor() {
    if (!this.deviceStore.deviceTypes()) {
      this.deviceStore.getDeviceTypesMethod();
    }
    // Resolve the current members first, then browse the owner partner's devices server-side
    // (mirroring the devices page). Fetching devices only after members are known guarantees the
    // first rendered page already knows which rows to disable — they never flip once visible.
    this.groupService
      .getInstallationGroupDevices(this.data.groupOid, MEMBER_FETCH_LIMIT)
      .pipe(takeUntilDestroyed())
      .subscribe({
        next: res => this.loadDevices(new Set((res.devices ?? []).map(m => m.deviceId))),
        error: () => this.loadDevices(new Set()),
      });
  }

  private loadDevices(memberIds: Set<number>): void {
    this.memberIds.set(memberIds);
    this.membersLoaded.set(true);
    this.deviceStore.getDevicesFilterMethod({ partnerOid: this.data.ownerPartnerOid });
  }

  protected onSearchChange(query: string): void {
    this.deviceStore.getDevicesFilterMethod({ deviceLDN: query });
  }

  protected onFilterChange(filters: Record<string, string[]>): void {
    this.deviceStore.getDevicesFilterMethod({
      deviceType: filters['deviceType']?.join(',') ?? '',
      installationState: filters['installationState']?.join(',') ?? '',
      pollingStatus: filters['pollingStatus']?.join(',') ?? '',
      simStatus: filters['simStatus']?.join(',') ?? '',
      connectionType: filters['connectionType']?.join(',') ?? '',
    });
  }

  protected onPageChange(event: PageEvent): void {
    this.deviceStore.getDevicesMethod(event.pageIndex);
  }

  protected onSelectedKeysChange(keys: Set<string | number>): void {
    this.selectedKeys.set(keys);
  }

  protected onCancel(): void {
    this.dialogRef.close(false);
  }

  protected onAdd(): void {
    const deviceIds = [...this.selectedKeys()].map(Number);
    if (!deviceIds.length) {
      return;
    }
    this.store.addDevicesToGroup({ groupOid: this.data.groupOid, deviceIds });
    this.dialogRef.close(true);
  }
}
